import { getFilterElementsDefault, sharpDateFormatter, useWorkplaceState } from '@finbeat/react-toolkit';
import {
    getPaymentsToClientsExcel,
    getPaymentsToClientsList,
    getPaymentToClient
} from 'axios/paymentsToClientsApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import DataTimeText from 'components/Common/DataTime';
import Money from 'components/Common/Money';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import {
    PaymentsToClientsFilter as FilterType,
    PaymentsToClientsTable,
    PaymentToClientInfo
} from 'models/api/paymentsToClients';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import PaymentsToClientsModal from './Modal/PaymentsToClientsModal';
import PaymentsToClientsFilter from './PaymentsToClientsFilter';

const PaymentsToClients = () => {
    const api = useApi();
    const { hasRole } = useAuth();
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const { dateTimeFormat } = useGeneralSettingsContext();
    const [isModalVisible, setModalVisible] = useState(false);

    const statuses = [
        {
            id: 'All',
            name: t('BackOfficeAll'),
        },
        {
            id: 'Success',
            name: t('BackOfficeSuccessful'),
        },
        {
            id: 'Error',
            name: t('BackOfficeErroneous'),
        },
    ];

    return (
        <BOWorkplace<PaymentsToClientsTable, PaymentToClientInfo, FilterType>
            pageTitle="BackOfficePaymentsToClients"
            filterParams={{
                sd: getStartOfTheDay(),
                ed: getEndOfTheDay(),
                status: 'Error',
            }}
            initColumns={[
                { key: 'clientId', name: <Trans i18nKey={'BackOfficeClientId'} /> },
                { key: 'id', name: <Trans i18nKey={'BackOfficeAppId'} /> },
                {
                    key: 'loanAmount',
                    name: <Trans i18nKey={'BackOfficeLoanAmount'} />,
                    formatter: ({ row: { loanAmount } }) => <Money value={loanAmount} />,
                },
                {
                    key: 'isRepeatClient',
                    name: <Trans i18nKey={'BackOfficeClientType'} />,
                    formatter: ({ row: { isRepeatClient } }) =>
                        isRepeatClient ? (
                            <Trans i18nKey="BackOfficeReapitedClient" />
                        ) : (
                            <Trans i18nKey="BackOfficeNewClient" />
                        ),
                },
                {
                    key: 'accountNumber',
                    name: <Trans i18nKey={'BackOfficeAccountNumber'} />,
                },
                {
                    key: 'clientFullName',
                    name: <Trans i18nKey={'BackOfficeFio'} />,
                },
                {
                    key: 'paymentStatus',
                    name: <Trans i18nKey={'BackOfficePaymentStatus'} />,
                },
                {
                    key: 'statusDt',
                    name: <Trans i18nKey={'BackOfficeStatusDate'} />,
                    formatter: ({ row: { statusDt } }) => <DataTimeText value={statusDt} />,
                },
            ]}
            getFilterElements={getFilterElementsDefault([
                {
                    id: 'sd',
                    formatter: (val) =>
                        `${t('BackOfficeDtFrom')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                {
                    id: 'ed',
                    formatter: (val) =>
                        `${t('BackOfficeTo')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                },
                {
                    id: 'status',
                    formatter: (val) => statuses.find((x) => x.id === val)?.name ?? val,
                },
            ])}
            showModal={
                hasRole('paymentsToClientsGetById') ? () => setModalVisible(true) : undefined
            }
            modal={{
                component: isModalVisible && (
                    <PaymentsToClientsModal
                        isOpen={isModalVisible}
                        toggle={() => setModalVisible(false)}
                    />
                ),
                fetchCurrentItemQueryFn: (item) => getPaymentToClient(api, item),
            }}
            filter={<PaymentsToClientsFilter statuses={statuses} />}
            fetchQueryFn={(filter) => getPaymentsToClientsList(api, filter)}
            fetchExcelQueryFn={(filter) => getPaymentsToClientsExcel(api, filter)}
            queryKey={['PaymentsToClients']}
            queryClient={queryClient}
            storagePath="paymentsToClients/columns"
            showExcelBtn={hasRole('paymentsToClientsExcel')}
        />
    );
};

export const usePaymentsToClientsWorkplaceState = () =>
    useWorkplaceState<PaymentsToClientsTable, PaymentToClientInfo>();

export default PaymentsToClients;
